/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type Mithril from 'mithril';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import type Article from '../models/Article';
import app from 'flarum/forum/app';

const ATTR_PRIORITIES = {
	category: 10,
	publisher: 9,
	language: 8,
	pages: 7,
	publishedAt: 5,
	isbn: 4,
} as const;

type ArticleInfoAttr = keyof typeof ATTR_PRIORITIES | 'ean';

function isDate(value: unknown): value is Date {
	return value instanceof Date;
}

export interface ArticleInfosAttrs extends ComponentAttrs {
	article: Article;
}

export default class ArticleInfos extends Component<ArticleInfosAttrs> {
	view() {
		return (
			<div className="ArticleInfos">
				<ul className="ArticleInfos-items">{listItems(this.items().toArray())}</ul>
			</div>
		);
	}

	items(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		for (const attr of Object.keys(ATTR_PRIORITIES) as (keyof typeof ATTR_PRIORITIES)[]) {
			this.addAttr(items, attr, ATTR_PRIORITIES[attr]);
		}

		return items;
	}

	protected addAttr(items: ItemList<Mithril.Children>, attr: ArticleInfoAttr, priority: number): void {
		const value = this.getAttrValue(attr);
		if (value) {
			items.add(
				attr,
				[<label>{app.translator.trans(`glowingblue-orell-articles.forum.article.${attr}`)}</label>, <span className="value">{value}</span>],
				priority
			);
		} else if (attr === 'isbn') {
			this.addAttr(items, 'ean', priority);
		}
	}

	protected getAttrValue(attr: ArticleInfoAttr): string | number | undefined {
		const value = this.attrs.article[attr]();

		if (isDate(value)) {
			const locale = app.translator.getLocale();
			return locale ? value.toLocaleDateString(locale) : value.toLocaleDateString();
		}

		return value;
	}
}
